import { http } from '@/api/http';

export interface FaqCategory {
  id: string;
  name: string;
  /** Lower sorts first when present */
  sort?: number;
}

export interface FaqItem {
  id: string;
  categoryId: string;
  question: string;
  answer: string;
  sort?: number;
}

/** One category card: title + FAQ rows under `category_id`. */
export interface FaqCategorySection {
  category: FaqCategory;
  items: FaqItem[];
}

function readString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function readNumber(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  }
  return undefined;
}

function payloadRecord(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  const root = raw as Record<string, unknown>;
  const d = root.data;
  if (d !== null && d !== undefined && typeof d === 'object' && !Array.isArray(d)) return d as Record<string, unknown>;
  return root;
}

function pickArray(o: Record<string, unknown>, keys: string[]): unknown[] {
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

function normalizeCategory(row: unknown, index: number): FaqCategory | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const id = readString(o, ['id', 'category_id', 'categoryId']) ?? `cat-${index}`;
  const name = readString(o, ['name', 'category_name', 'title', 'label']) || 'FAQ';
  const sort = readNumber(o, ['sort', 'sort_order', 'sortOrder', 'order_num', 'orderNum']);
  return { id, name, sort };
}

function normalizeFaqItem(row: unknown, index: number): FaqItem | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const categoryId =
    readString(o, ['category_id', 'categoryId', 'cid', 'faq_category_id']) ?? '';
  const question =
    readString(o, ['question', 'title', 'q', 'faqTitle', 'itemTitle', 'name', 'subject']) ?? '';
  const answer =
    readString(o, ['answer', 'content', 'a', 'faqContent', 'itemContent', 'description', 'detail']) ??
    '';
  if (!question || !answer) return null;
  const id = readString(o, ['id', 'itemId', 'faqId']) ?? `faq-${index}`;
  const sort = readNumber(o, ['sort', 'sort_order', 'sortOrder', 'order_num', 'orderNum']);
  return { id, categoryId, question, answer, sort };
}

function sortCategories(cats: FaqCategory[]): FaqCategory[] {
  return [...cats].sort((a, b) => {
    const sa = a.sort ?? Number.MAX_SAFE_INTEGER;
    const sb = b.sort ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    return 0;
  });
}

function sortItems(items: FaqItem[]): FaqItem[] {
  return [...items].sort((a, b) => {
    const sa = a.sort ?? Number.MAX_SAFE_INTEGER;
    const sb = b.sort ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    return 0;
  });
}

function buildSections(categories: FaqCategory[], items: FaqItem[]): FaqCategorySection[] {
  const byCat = new Map<string, FaqItem[]>();
  for (const it of items) {
    const k = it.categoryId;
    if (!byCat.has(k)) byCat.set(k, []);
    byCat.get(k)!.push(it);
  }

  const ordered = sortCategories(categories);
  const sections: FaqCategorySection[] = [];

  for (const cat of ordered) {
    const list = sortItems(byCat.get(cat.id) ?? []);
    sections.push({ category: cat, items: list });
    byCat.delete(cat.id);
  }

  for (const [cid, rest] of byCat) {
    if (!rest.length) continue;
    sections.push({
      category: { id: cid, name: 'Other' },
      items: sortItems(rest),
    });
  }

  return sections;
}

function parseFaqPayload(payload: Record<string, unknown>): FaqCategorySection[] {
  const catRows = pickArray(payload, ['category_list', 'categoryList', 'categories']);
  const itemRows = pickArray(payload, ['list', 'items', 'faqList', 'rows']);

  const categories = catRows
    .map((row, i) => normalizeCategory(row, i))
    .filter((x): x is FaqCategory => x !== null);

  const items = itemRows
    .map((row, i) => normalizeFaqItem(row, i))
    .filter((x): x is FaqItem => x !== null);

  if (categories.length === 0 && items.length > 0) {
    return [{ category: { id: '_all', name: 'FAQ' }, items: sortItems(items) }];
  }

  if (categories.length === 0) {
    return [];
  }

  return buildSections(categories, items);
}

/** Expects payload (often under `data`) with `category_list` + `list`; items use `category_id` to match category `id`. */
export async function fetchFaqItemList(): Promise<FaqCategorySection[]> {
  const raw = await http.post<unknown>('/system/public/faq/item/list');
  return parseFaqPayload(payloadRecord(raw));
}
